<script>
const PADDING_SIZE = Object.freeze({
  NORMAL: "normal",
  BIG: "big",
});

const BORDER_RADIUS = Object.freeze({
  SMALL: "small",
  BIG: "big",
});
</script>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { BIconCheck } from "bootstrap-icons-vue";
import { threadStatusIconHollowMap } from "./ThreadStatusDropdown2.vue";

const props = defineProps({
  paddingSize: {
    type: String,
    validator: (prop) => Object.values(PADDING_SIZE).includes(prop),
    default: PADDING_SIZE.NORMAL,
  },
  borderRadius: {
    type: String,
    validator: (prop) => Object.values(BORDER_RADIUS).includes(prop),
    default: BORDER_RADIUS.BIG,
  },
  label: {
    type: String,
    default: "Select",
  },
  currentSelectedOption: {
    type: [String, Number],
    default: null,
  },
  header: {
    type: String,
    default: "Choose an option",
  },
  autoClose: {
    type: [Boolean, String],
    default: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  visibility: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [String, Number, Array],
    default: null,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  hasSemiBoldLabel: {
    type: Boolean,
    default: false,
  },
  showCheckedIcon: {
    type: Boolean,
    default: true,
  },
  alignEnd: {
    type: Boolean,
    default: true,
  },
  allowToOpen: {
    type: Boolean,
    default: true,
  },
  allowToClose: {
    type: Boolean,
    default: true,
  },
  appendToBody: {
    type: Boolean,
    default: false,
  },
  shouldHighlightSelectedOption: {
    type: Boolean,
    default: true,
  },
  chevronDownStroke: {
    type: String,
    default: "#8C8C8C",
  },
  chevronTriggersVisibility: {
    type: Boolean,
    default: false,
  },
  displaySelectedOption: {
    type: Boolean,
    default: true,
  },
  hideSelectedOption: {
    type: Boolean,
    default: false,
  },
  hideChevron: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  searchValue: {
    type: String,
    default: "",
  },
  sortOptions: {
    type: Boolean,
    default: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  displayNoOptions: {
    type: Boolean,
    default: true,
  },
  valueRequired: {
    type: Boolean,
    default: false,
  },
  theme: {
    type: String,
    default: "gray",
  },
  counts: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits([
  "update:modelValue",
  "update:visibility",
  "button-content-clicked",
  "update:searchValue",
]);

const selectedOption = ref(props.modelValue);
const dropdownVisible = ref(false);
const dropdownRef = ref(null);

const selectedOnThisFocus = ref([]);

const searchValueRef = ref();

const debounceUpdateFilterFullName = ref("");

const paddingClass = computed(() => {
  return props.paddingSize === PADDING_SIZE.BIG
    ? "padding-big"
    : "padding-normal";
});

const borderClass = computed(() => {
  return props.borderRadius === BORDER_RADIUS.BIG
    ? "border-big"
    : "border-small";
});

const labelWithCount = computed(() => {
  const showCount = !!Object.values(props.counts).length
  return showCount ? `${props.label} (${totalCount.value})` : props.label
})

const commitStatus = () => {
  emit("update:modelValue", selectedOption.value)
}

const onHide = () => {
  if (!props.allowToClose) {
    evt.preventDefault()
    return
  }
  commitStatus()
}

const totalCount = computed(()=>{
  if(!props.counts) return 0

  // loop through the options and get the value from the count object
  // if count doesnt exist for that option return 0, probably use reduce here

  const count = props.options.reduce((acc, cur) => {
    const curCount = props.counts[cur.value] ?? 0
    return acc + curCount
  }, 0)

  return count
})

const selectOption = (option) => {
  if (props.multiple) {
    if (!Array.isArray(selectedOption.value)) {
      selectedOption.value = [];
    }

    if (selectedOption.value.includes(option.value)) {
      selectedOption.value = selectedOption.value.filter(
        (s) => s !== option.value
      );

      // emit("update:modelValue", selectedOption.value);
      return;
    }

    selectedOption.value = [...selectedOption.value, option.value];
    // emit("update:modelValue", selectedOption.value);
    return;
  }

  if (selectedOption.value === option.value && !props.valueRequired) {
    selectedOption.value = null;
    // emit("update:modelValue", null);
    return;
  }
  selectedOption.value = option.value;
  // emit("update:modelValue", selectedOption.value);
};

const toggleDropdown = () => {
  if (props.allowToOpen && !dropdownVisible.value) dropdownVisible.value = true;
  else if (props.allowToClose && dropdownVisible.value)
    dropdownVisible.value = false;
};

const handleDropdownButtonClick = (event) => {
  event.stopPropagation();
  emit("button-content-clicked");
};

const onButtonContentClick = () => {
  if (!props.chevronTriggersVisibility) {
    toggleDropdown();
  }
  emit("button-content-clicked");
};

const onChevronClick = () => {
  toggleDropdown();
};

watch(
  dropdownVisible,
  (val) => {
    emit("update:visibility", val);
  },
  {
    immediate: true,
  }
);

watch(
  () => props.visibility,
  (val) => {
    dropdownVisible.value = val;
  },
  {
    immediate: true,
  }
);

watch(
  () => searchValueRef.value,
  (val) => {
    emit("update:searchValue", val);
  }
);

watch(
  () => props.modelValue,
  (newValue, prevValue) => {
    selectedOption.value = newValue;
  }
);

watch(
  () => props.searchValue,
  (val) => {
    searchValueRef.value = val;
  },
  {
    immediate: true,
  }
);

onMounted(() => {
  if (dropdownRef.value) {
    const button = dropdownRef.value.$el.querySelector("button");
    if (button) {
      button.addEventListener("click", handleDropdownButtonClick);
    }
  }
});

onBeforeUnmount(() => {
  if (dropdownRef.value) {
    const button = dropdownRef.value.$el.querySelector("button");
    if (button) {
      button.removeEventListener("click", handleDropdownButtonClick);
    }
  }
});

const sortedOptions = computed(() => {
  const searchValue = searchValueRef.value?.toLowerCase() || "";

  const selectedValues = Array.isArray(selectedOption.value)
    ? selectedOption.value
    : [selectedOption.value];

  const filteredOptions = props.options.filter((item) => {
    if (typeof item === "object" && item !== null && !Array.isArray(item)) {
      return item.text.toLowerCase().includes(searchValue);
    }
    return String(item).toLowerCase().includes(searchValue);
  });

  // Filter selected and unselected items
  let selectedItems = filteredOptions.filter(
    (option) =>
      selectedValues.includes(option.value) &&
      !selectedOnThisFocus.value.includes(option.value)
  );

  let unselectedItems = filteredOptions.filter(
    (option) =>
      !selectedValues.includes(option.value) ||
      selectedOnThisFocus.value.includes(option.value)
  );

  if (props.sortOptions) {
    selectedItems = selectedItems.sort((a, b) => a.text.localeCompare(b.text));
    unselectedItems = unselectedItems.sort((a, b) =>
      a.text.localeCompare(b.text)
    );
  }

  if (props.hideSelectedOption) return unselectedItems;

  if (props.sortOptions) {
    // Merge the sorted lists
    return [...selectedItems, ...unselectedItems];
  }
  return filteredOptions;
});

const checkIsOptionActive = (option) => {
  if (props.multiple) {
    if (!Array.isArray(selectedOption.value)) {
      selectedOption.value = [];
    }
    return selectedOption.value.includes(option.value);
  }
  return selectedOption.value === option.value;
};

const boundaryProp = computed(() => {
  return props.appendToBody ? "viewport" : "scrollParent";
});

watch(
  dropdownVisible,
  async () => {
    if (!dropdownVisible.value) {
      selectedOnThisFocus.value = [];
    }
  },
  {
    deep: true,
  }
);

defineExpose({
  sortedOptions,
});
</script>

<template>
  <b-dropdown
    ref="dropdownRef"
    :end="alignEnd"
    :auto-close="autoClose"
    :class="[paddingClass, borderClass, theme, { 'is-visible': dropdownVisible }]"
    v-model="dropdownVisible"
    @show="(evt) => (allowToOpen ? null : evt.preventDefault())"
    @hide="onHide"
    :boundary="boundaryProp"
  >
    <template #button-content>
      <span
        @click.stop="onButtonContentClick"
        class="d-flex align-items-center column-gap-2"
      >
        <IconCircleFill class="circle-icon" />
        <span
          class="d-flex align-items-center column-gap-2 label text-neutral-400 font-size-xs"
          >{{ labelWithCount }}
          <span
            v-if="currentSelectedOption && displaySelectedOption"
            class="text-neutral-300 fw-semibold font-size-xs"
          >
            ({{ currentSelectedOption }})
          </span>
        </span>
        <IconChevronDown
          v-if="!hideChevron"
          class="icon-chevron-down"
          :stroke="chevronDownStroke"
          @click.stop="onChevronClick"
        />
      </span>
    </template>

    <b-dropdown-header v-if="showHeader" @click.stop id="dropdown-header-label">
      {{ header }}
    </b-dropdown-header>

    <div v-if="showSearch" style="padding: 0 8px 8px 8px">
      <TextInput
        @click.stop
        type="search"
        placeholder="Search..."
        v-model="searchValueRef"
        @update:model-value="debounceUpdateFilterFullName"
      />
    </div>
    <template v-if="sortedOptions.length">
      <div class="dropdown-items__content" :class="{ 'mt-2': !showHeader }">
        <slot :selectedOption="selectedOption">
          <!-- IDK why auto-close=outside or false still closes when an option is
               selected, but capturing here seems to stop it? (dropdown-items__content
               div also needs @click.stop) That said, too risky, so I am not implementing
               it for now. -->
          <b-dropdown-item-button
            v-for="option in sortedOptions"
            :key="option.value"
            @click.stop="selectOption(option)"
            :active="shouldHighlightSelectedOption && checkIsOptionActive(option)"
          >
            
            <div class="d-flex align-items-center column-gap-2">
              <IconCircleFill class="circle-icon" :isHollow="threadStatusIconHollowMap[option.value] || false" />
              <slot :name="`pre-${option.value}-content`" />

              <span
                class="d-flex flex-grow-1 justify-content-between align-items-center font-size-sm"
              >
                <span>{{ option.text || "N/A" }} ({{ counts[option.value] || 0 }})</span>
                <b-icon-check
                  class="icon-check"
                  v-if="showCheckedIcon && checkIsOptionActive(option)"
                />
              </span>
            </div>
          </b-dropdown-item-button>
        </slot>
      </div>
    </template>

    <span
      v-else-if="!sortedOptions.length && displayNoOptions"
      class="dropdown-header"
      >No Options found</span
    >
    <b-dropdown-item v-if="$slots['item-content']">
      <slot name="item-content" />
    </b-dropdown-item>
    <template v-if="$slots['custom-content']">
      <slot name="custom-content" />
    </template>
  </b-dropdown>
</template>

<style scoped lang="scss">
.dropdown {
  stroke-width: 3px;
  
  &.padding-normal {
    :deep(.dropdown-toggle) {
      padding: 0.25rem 0.5rem;
    }
  }

  &.padding-big {
    :deep(.dropdown-toggle) {
      padding: 0.5rem 0.75rem;
    }
  }

  &.border-small {
    :deep(.dropdown-toggle) {
      border-radius: 8px;
    }
  }

  &.border-big {
    :deep(.dropdown-toggle) {
      border-radius: 20px;
    }
  }

  &-items__content {
    padding: 0 8px 8px 8px;
  }

  &.is-visible {
    :deep(.dropdown-toggle) {
      background: $neutral-50;
      border: 1px solid $neutral-200;
    }
  }

  :deep(.dropdown-toggle) {
    border: 1px solid $neutral-150;
    font-size: 0.875rem;
    cursor: pointer;
    background: white;
    // color: $neutral-400;
    height: 100%;

    .icon-chevron-down {
      width: 0.75rem;
      height: 0.75rem;
    }

    &:active {
      background: $neutral-100;
      border: 1px solid $neutral-200;
    }
    &:hover {
      background: $neutral-50;
      border: 1px solid $neutral-200;
    }

    &::after {
      content: "";
      display: none;
    }

    &:focus-visible {
      outline: none; /* Remove the outline */
      box-shadow: none;
    }

  }

    // Fill the colors here
  &.red {
    :deep(.dropdown-toggle) {
      background-color: $red-0;
      border: 1px solid $red-50;

      &:hover {
        border: 1px solid $red-100;
      }

      &.show {
        :deep(.dropdown-toggle) {
          background-color: $red-50;
          border: 1px solid $red-150;
        }
      }
    }

    // I want to target all .circle-icons that are children of red
    :deep(.circle-icon) {
      stroke: $red-200;
      fill: $red-200;
    }
  }

  &.green {
    :deep(.dropdown-toggle) {
      background-color: $green-0;
      border: 1px solid $green-50;

      &:hover {
        border: 1px solid $green-100;
      }

      &.show {
        background-color: $green-50;
        border: 1px solid $green-150;
      }
    }

    :deep(.circle-icon) {
      stroke: $green-150;
      fill: $green-150;
    }
  }

  &.yellow {
    :deep(.dropdown-toggle) {
      background-color: $yellow-0;
      border: 1px solid $yellow-50;

      &:hover {
        border: 1px solid $yellow-100;
      }

      &.show {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
    }

    :deep(.circle-icon) {
      stroke: $yellow-250;
      fill: $yellow-250;
    }
  }

  &.blue {
    :deep(.dropdown-toggle) {
      background-color: var(--primary-0);
      border: 1px solid var(--primary-50);

      &:hover {
        border: 1px solid var(--primary-100);
      }

      &.show {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
    }

    :deep(.circle-icon) {
      stroke: var(--primray-250);
      fill: var(--primary-250);
    }
  }

  &.gray {
    :deep(.dropdown-toggle) {
      background-color: $neutral-50;
      border: 1px solid $neutral-150;

      &:hover {
        border: 1px solid $neutral-150;
      }

      &.show {
        background-color: $neutral-100;
        border: 1px solid $neutral-250;
      } 
    }

    :deep(.circle-icon) {
      stroke: $neutral-250;
      fill: $neutral-250;
    }
  }

  :deep(.dropdown-menu) {
    margin-top: 4px;
    border: 1px solid $bluish-gray-300;
    box-shadow: 0 2px 12px 00 $box-shadow-color;
    border-radius: 10px;
    //padding: 0 8px 8px 8px;
    max-height: 15.625rem !important;
    overflow: auto;

    li {
      margin-top: 4px;

      &:first-of-type {
        margin-top: 0;
        //TODO margin-bottom: 8px;
      }
    }

    &:focus-visible {
      outline: none; /* Remove the outline */
    }

    .dropdown-header {
      font-size: $font-size-xs;
      font-weight: 400;
      color: $neutral-400;
      padding: 12px 16px 10px 16px;

    }

    .dropdown-item {
      padding: 12px;
      color: $neutral-400;
      border-radius: 0.625rem;
      font-size: $font-size-sm;

      &.active {
        background: var(--primary-0);
        border: 1px solid var(--primary-50);

        .icon-check {
          width: 24px;
          height: 24px;
          fill: var(--primary-250);
        }
      }

      &:hover {
        background: var(--primary-0);
      }

      &:focus {
        background: var(--primary-0);
      }
    }

  }


}
</style>
